import { useState, useMemo } from 'react'
import clsx from 'clsx'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { subColsMap, styles, orderableCols } from './data'
import { colsBalanceGeneral as cols } from '../../../components/modules/data/headersTables'
import { Text } from '../../../components/modules/ui'
import { useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'

export default function Table({
  rows,
  columnSpacing = 8,
  rowSpacing = 0,
  secondColumnWidth = 250,
  defaultColumnWidth = 80,
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [order, setOrder] = useState({
    col: 'N° Cuenta',
    field: 'numCuenta',
    direction: 'asc',
  })

  // ① Separamos resumen vs datos, ordenamos solo los datos y luego concatenamos
  const sortedRows = useMemo(() => {
    if (!rows?.length) return []

    // Separar filas de datos (tienen numCuenta) vs resúmenes (no lo tienen)
    const dataRows = rows.filter(r => r.numCuenta)
    const summaryRows = rows.filter(r => !r.numCuenta)

    const { field, direction } = order
    const mult = direction === 'asc' ? 1 : -1

    // Ordenar solo dataRows
    dataRows.sort((a, b) => {
      const aVal = a[field] ?? ''
      const bVal = b[field] ?? ''

      if (field === 'numCuenta') {
        const segA = String(aVal).split('-').map(n => parseInt(n, 10) || 0)
        const segB = String(bVal).split('-').map(n => parseInt(n, 10) || 0)
        for (let i = 0; i < Math.max(segA.length, segB.length); i++) {
          if (segA[i] !== segB[i]) {
            return (segA[i] - segB[i]) * mult
          }
        }
        return 0
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * mult
      }

      const numA = parseFloat(String(aVal).replace(/\./g, '').replace(',', '.'))
      const numB = parseFloat(String(bVal).replace(/\./g, '').replace(',', '.'))
      if (!isNaN(numA) && !isNaN(numB)) {
        return (numA - numB) * mult
      }

      return String(aVal)
        .localeCompare(String(bVal), 'es', { numeric: true }) * mult
    })

    // Finalmente: datos primero, luego las filas de resumen
    return [...dataRows, ...summaryRows]
  }, [rows, order])

  const leafCols = useMemo(() => {
    return cols.reduce((acc, col) => {
      const subs = subColsMap[col]
      if (subs) subs.forEach(() => acc.push(col))
      else acc.push(col)
      return acc
    }, [])
  }, [])

  const tableStyle = {
    borderCollapse: 'separate',
    borderSpacing: `${columnSpacing}px ${rowSpacing}px`,
    tableLayout: 'fixed',
    width: '100%',
  }

  if (sortedRows.length === 0) {
    return <Text className="grid place-items-center py-12 text-muted font-sans">No hay datos</Text>
  }

  const handleSort = ({ col, field }) => {
    const isAsc = order.col === col && order.direction === 'asc'
    setOrder({ col, field, direction: isAsc ? 'desc' : 'asc' })
  }
  const searchColsIndex = col => orderableCols.find(item => item.col === col)

  if (isMobile) {
    const metrics = [
      { key: 'debe', label: 'Debe' },
      { key: 'haber', label: 'Haber' },
      { key: 'deudor', label: 'Deudor' },
      { key: 'acreedor', label: 'Acreedor' },
      { key: 'activo', label: 'Activo' },
      { key: 'pasivo', label: 'Pasivo' },
      { key: 'perdida', label: 'Pérdida' },
      { key: 'ganancia', label: 'Ganancia' },
    ]
    const formatValue = (val) =>
      Number(val ?? 0).toLocaleString('es-CL')
    const renderMetric = (label, value) => (
      <div className="rounded-lg bg-surface/70 border border-border px-2 py-2 min-w-0">
        <div className="text-[11px] text-muted">{label}</div>
        <div className="text-sm font-semibold text-text break-words">{value}</div>
      </div>
    )

    return (
      <div className="space-y-3 font-sans text-text">
        {sortedRows.map((row, i) => {
          const isSummary = !row.numCuenta
          const title = row.numCuenta
            ? `${row.numCuenta} ${row.nameCuenta ?? ''}`.trim()
            : row.nameCuenta || 'Resumen'
          return (
            <div
              key={i}
              className={clsx(
                'rounded-2xl border border-border p-3',
                isSummary ? 'bg-surface/70' : 'bg-surface'
              )}
            >
              <div className="text-xs font-semibold text-text">{title}</div>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {metrics.map(metric => (
                  <div key={metric.key}>
                    {renderMetric(metric.label, formatValue(row[metric.key]))}
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <div className="h-[650px] overflow-y-auto overflow-x-auto border border-border rounded bg-surface font-sans text-[11px] sm:text-xs text-text">
        <table className={styles.table} style={tableStyle}>
          <colgroup>
            {leafCols.map((_, idx) => (
              <col
                key={idx}
                style={{
                  width:
                    idx === 1
                      ? `${secondColumnWidth}px`
                      : `${defaultColumnWidth}px`,
                }}
              />
            ))}
          </colgroup>

          <thead className={styles.thead}>
            <tr>
              {cols.map((col, i) => {
                const sortable = !!searchColsIndex(col)
                return (
                  <th
                    key={i}
                    className={clsx(
                      styles.th,
                      'text-center',
                      { 'cursor-pointer': sortable }
                    )}
                    onClick={() =>
                      sortable && handleSort(searchColsIndex(col))
                    }
                    colSpan={subColsMap[col]?.length || 1}
                  >
                    {col}
                    {sortable && (
                      <KeyboardArrowDownIcon
                        className={clsx(
                          'transition-all ml-2',
                          { 'opacity-40': order.col !== col },
                          {
                            'rotate-180':
                              order.col === col &&
                              order.direction === 'desc',
                          }
                        )}
                      />
                    )}
                  </th>
                )
              })}
            </tr>
            <tr>
              {cols.map((col, i) =>
                subColsMap[col] ? (
                  subColsMap[col].map(subCol => (
                    <th
                      key={`${col}-${subCol}`}
                      className={styles.th}
                    >
                      {subCol.charAt(0).toUpperCase() +
                        subCol.slice(1)}
                    </th>
                  ))
                ) : (
                  <th key={i} className={styles.th} />
                )
              )}
            </tr>
          </thead>

          <tbody className={styles.tbody}>
            {sortedRows.map((row, i) => (
              <tr className={styles.tr} key={i}>
                <td className={styles.td}>
                  <Text className={styles.boldText}>
                    {row.numCuenta || ''}
                  </Text>
                </td>
                <td
                  className={styles.td}
                  style={{ whiteSpace: 'normal' }}
                >
                  <Text className={styles.boldText}>
                    {row.nameCuenta}
                  </Text>
                </td>
                {cols.map(col =>
                  subColsMap[col]
                    ? subColsMap[col].map(subCol => (
                        <td
                          key={`${col}-${subCol}`}
                          className={clsx(
                            styles.td,
                            'text-right'
                          )}
                        >
                          <Text className={styles.text}>
                            {row[subCol]?.toLocaleString(
                              'es-CL'
                            ) ?? ''}
                          </Text>
                        </td>
                      ))
                    : null
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
